// src/pages/QuestionPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Progress,
  Spin,
  Checkbox,
  notification,
  Collapse,
} from "antd";
import {
  useGetFormDetailsQuery,
  useGetFormQuestionsQuery,
} from "../../store/api/apiSlice";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import CustomVideoPlayer from "../../components/common/CustomVideoPlayer";
import DynamicInput from "../../components/survey/DynamicInput";
import { motion, AnimatePresence } from "framer-motion";
import { AnanymousIcon, OrganizationsIcon } from "../../assets/svgs/icons";
import { RightOutlined } from "@ant-design/icons";
import {
  getFormOrganizationLogoUrl,
  pickMediaUrl,
} from "../../utils/formTabs";
import {
  findConditionalNextQuestion,
  getVisibleInputEntries,
  isCurrentQuestionConditionSource,
  isInputConditionMet,
} from "../../utils/inputConditions";
import { isRadioListInputType, isRadioListRequiredEmpty } from "../../utils/radioList";
import { getQuestionTitleNumber } from "../../utils/questionNumber";
import {
  getUniqueSections,
  normalizeSectionLabel,
  SurveySectionLabels,
} from "../../components/survey/SurveySectionLabels";

const resolveOrgHeaderImageUrl = (organization, formRoot) =>
  getFormOrganizationLogoUrl(formRoot) ||
  pickMediaUrl(organization?.logo) ||
  pickMediaUrl(organization?.image) ||
  pickMediaUrl(formRoot?.image) ||
  null;

const QuestionPage = () => {
  const { formId, questionNumber } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedResponses = Form.useWatch("responses", form) || [];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [responseId, setResponseId] = useState(null);
  const [direction, setDirection] = useState(
    localStorage.getItem("surveyAnimationDirection") || "vertical",
  );
  const { data: formData, isLoading: formLoading } =
    useGetFormDetailsQuery(formId);
  const { data: questionsData, isLoading: questionsLoading } =
    useGetFormQuestionsQuery(formId);
  const organizationTermsRoute = `/organization-terms-and-conditions?formId=${encodeURIComponent(
    formId,
  )}`;
  const organizationPrivacyRoute = `/organization-privacy-policy?formId=${encodeURIComponent(
    formId,
  )}`;

  const [responses, setResponses] = useState({});
  const [orgLogoError, setOrgLogoError] = useState(false);
  const orgLogoUrl = resolveOrgHeaderImageUrl(
    formData?.data?.organization,
    formData?.data,
  );
  const sortedQuestions = [...(questionsData?.data || [])].sort(
    (a, b) => a.number - b.number,
  );

  const questions = sortedQuestions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const questionTitleNumber = getQuestionTitleNumber(
    currentQuestion,
    currentQuestionIndex,
  );
  const totalQuestions = questions.length;
  const sectionLabels = getUniqueSections(questions);
  const activeSection = normalizeSectionLabel(currentQuestion?.section);
  const uploadedRecordVideos = (currentQuestion?.response || [])
    .map((input, idx) => ({
      inputId: input?._id || `record-video-${idx}`,
      isRecordVideo: input?.input_type === "record-video",
      value: watchedResponses?.[idx],
    }))
    .filter(
      (item) =>
        item.isRecordVideo &&
        typeof item.value === "string" &&
        item.value.trim().length > 0,
    );

  const hasUploadedRecordVideo = uploadedRecordVideos.length > 0;

  useEffect(() => {
    if (!currentQuestion?.response?.length) return;

    currentQuestion.response.forEach((input, index) => {
      if (isInputConditionMet(input, watchedResponses)) return;

      const current = form.getFieldValue(["responses", index]);

      if (current === undefined || current === null || current === "") return;

      if (Array.isArray(current) && current.length === 0) return;

      form.setFieldValue(["responses", index], undefined);
    });
  }, [watchedResponses, currentQuestion, form]);

  const handleAnonymousChange = (e) => {
    if (!formData?.data?.anonymous) {
      setIsAnonymous(e.target.checked);
    }
  };

  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
    localStorage.setItem("surveyAnimationDirection", newDirection);
  };

  useEffect(() => {
    if (formData?.data?.anonymous) {
      setIsAnonymous(true);
    }
  }, [formData]);

  useEffect(() => {
    localStorage.removeItem(`survey_${formId}_responses`);
    localStorage.removeItem(`survey_${formId}_responseId`);
    localStorage.removeItem("SurevyUserId");
    localStorage.removeItem("surveyUserData");

    setResponses({});
    setResponseId(null);
    setIsAnonymous(Boolean(formData?.data?.anonymous));

    form.resetFields();
    form.setFieldsValue({
      terms: false,
      anonymous: false,
      responses: [],
    });
  }, [formId, form]);

  useEffect(() => {
    if (!localStorage.getItem("surveyAnimationDirection")) {
      localStorage.setItem("surveyAnimationDirection", "vertical");
      setDirection("vertical");
    }
  }, []);

  useEffect(() => {
    if (questions) {
      const newIndex = questions.findIndex(
        (q) => q.number?.toString() === questionNumber,
      );

      if (newIndex !== -1) {
        setCurrentQuestionIndex(newIndex);
      } else {
        setCurrentQuestionIndex(0);
      }
    }
  }, [questionNumber, questionsData]);

  useEffect(() => {
    form.resetFields();

    if (currentQuestion && responses[currentQuestion._id]) {
      try {
        const questionResponses = responses[currentQuestion._id];
        const formattedResponses = currentQuestion.response.map((_, index) => {
          const responseKey = Object.keys(questionResponses)[index];
          return questionResponses[responseKey]?.value || null;
        });

        form.setFieldsValue({
          responses: formattedResponses,
        });

        if (currentQuestionIndex === 0) {
          form.setFieldsValue({
            anonymous: questionResponses.anonymous || false,
            // Never restore Terms as checked from cache/session.
            terms: false,
          });
        }
      } catch (error) {
        console.warn("Error setting form values:", error);
        form.resetFields();
        form.setFieldsValue({ terms: false });
      }
    } else if (currentQuestionIndex === 0) {
      form.setFieldsValue({ terms: false, anonymous: false });
    }
  }, [currentQuestion, responses, currentQuestionIndex, formId, form]);

  if (formLoading || questionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const handleSubmit = async (values) => {
    try {
      if (formData.data.anonymous) {
        values.anonymous = true;
      }

      if (!values.responses) {
        values.responses = [];
      }

      let hasEmptyRequiredFields = false;

      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (input.require !== false) {
          if (
            !response ||
            (input.input_type === "checkbox" && response.length === 0) ||
            (input.input_type === "slider" && !response) ||
            (input.input_type === "radio" && !response) ||
            (input.input_type === "record-video" && !response) ||
            (input.input_type === "file" && !response) ||
            (input.input_type === "multiple-files" &&
              (!response ||
                (Array.isArray(response) && response.length === 0))) ||
            isRadioListRequiredEmpty(input, response)
          ) {
            form.setFields([
              {
                name: ["responses", index.toString()],
                errors: [
                  `Please ${
                    input.input_type === "record-video"
                      ? "record a video before proceeding"
                      : input.input_type === "file" ||
                          input.input_type === "multiple-files"
                        ? "upload a file"
                        : input.input_type === "slider"
                          ? "select a rating"
                          : input.input_type === "checkbox"
                            ? "select at least one option"
                            : input.input_type === "radio"
                              ? "select an option"
                              : isRadioListInputType(input.input_type)
                                ? "select an option for every row"
                                : "provide a response"
                  }`,
                ],
              },
            ]);

            hasEmptyRequiredFields = true;
          }
        }
      });

      if (hasEmptyRequiredFields) {
        return;
      }

      setIsSubmitting(true);
      let formattedResponses = {};

      currentQuestion?.response?.forEach((input, index) => {
        if (!isInputConditionMet(input, values.responses)) {
          return;
        }

        const response = values.responses?.[index];

        if (response) {
          const responseKey =
            input.input_title || `question_${currentQuestion._id}_${index}`;

          if (input.input_type === "record-video" && response instanceof Blob) {
            formattedResponses[responseKey] = {
              type: "record-video",
              value: "preview_video_url_" + Date.now(),
              inputId: input._id,
            };
          } else if (input.input_type === "file" && response instanceof Blob) {
            formattedResponses[responseKey] = {
              type: "file",
              value: "preview_file_url_" + Date.now(),
              inputId: input._id,
            };
          } else if (input.input_type === "multiple-files") {
            const items = Array.isArray(response) ? response : [response];

            formattedResponses[responseKey] = {
              type: "multiple-files",
              value: items.map((item, itemIndex) =>
                item instanceof Blob
                  ? `preview_file_url_${Date.now()}_${itemIndex}`
                  : item,
              ),
              inputId: input._id,
            };
          } else if (input.input_type === "checkbox") {
            formattedResponses[responseKey] = {
              type: "checkbox",
              value: Array.isArray(response) ? response : [response],
              inputId: input._id,
            };
          } else {
            formattedResponses[responseKey] = {
              type: input.input_type,
              value: response,
              inputId: input._id,
            };
          }
        }
      });

      if (currentQuestionIndex === 0 && !responseId) {
        const tempId = `preview_${Date.now()}`;
        setResponseId(tempId);
        localStorage.setItem(`survey_${formId}_responseId`, tempId);
      }

      const updatedResponses = {
        ...responses,
        [currentQuestion._id]: {
          ...formattedResponses,
          ...(currentQuestionIndex === 0
            ? {
                anonymous: values.anonymous || false,
                terms: values.terms || false,
              }
            : {}),
        },
      };

      setResponses(updatedResponses);
      localStorage.setItem(
        `survey_${formId}_responses`,
        JSON.stringify(updatedResponses),
      );

      const conditionalNextQuestion = findConditionalNextQuestion(
        questions,
        currentQuestion,
        values.responses,
      );

      if (conditionalNextQuestion) {
        navigate(`/admin/question/${formId}/${conditionalNextQuestion.number}`);
      } else if (currentQuestionIndex < totalQuestions - 1) {
        const isConditionalQuestion = isCurrentQuestionConditionSource(
          questions,
          currentQuestion,
        );

        if (isConditionalQuestion) {
          const currentNumber = currentQuestion.number;
          const remainingQuestions = questions
            .filter((q) => q.number > currentNumber)
            .sort((a, b) => a.number - b.number);

          if (remainingQuestions.length > 0) {
            navigate(
              `/admin/question/${formId}/${remainingQuestions[0].number}`,
            );
          } else {
            navigate(`/admin/question/thank-you?formId=${formId}`);
          }
        } else {
          const nextQuestion = questions[currentQuestionIndex + 1];
          navigate(`/admin/question/${formId}/${nextQuestion.number}`);
        }
      } else {
        navigate(`/admin/question/thank-you?formId=${formId}`);
      }

      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);

      notification.error({
        message: "Error",
        description: error.message || "Failed to save response",
      });
    }
  };

  const getAnimationProps = () => {
    const dir = localStorage.getItem("surveyAnimationDirection") || "vertical";

    if (dir === "vertical") {
      return {
        initial: { y: 40, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: -40, opacity: 0 },
        transition: { duration: 0.3 },
      };
    } else {
      return {
        initial: { x: 40, opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: -40, opacity: 0 },
        transition: { duration: 0.3 },
      };
    }
  };

  const getNavigationButtonText = () => {
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
    const hasMultipleQuestions = totalQuestions > 1;
    return currentQuestion?.buttonType === "start" && !hasUploadedRecordVideo
      ? "Start"
      : currentQuestion?.buttonType === "next"
        ? "Next"
        : currentQuestion?.buttonType === "finish"
          ? "Submit"
          : isLastQuestion
            ? "Submit"
            : hasMultipleQuestions
              ? "Next"
              : "Next";
  };

  const BottomNavigation = () => {
    if (direction !== "vertical" && !currentQuestion?.showButton) {
      return null;
    }

    const buttonText = getNavigationButtonText();
    const shouldHideSubmitButton =
      formData?.data?.status !== "active" && buttonText === "Submit";
    if (shouldHideSubmitButton) return null;

    return (
      <div className="mt-4 flex w-full justify-center">
        <Button
          type="primary"
          loading={isSubmitting}
          htmlType="submit"
          className="cursor-pointer w-full shadow-lg flex items-center justify-center px-8 py-2 rounded-2xl"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {buttonText}
        </Button>
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentQuestion?._id || "loading"}
        {...getAnimationProps()}
        className="box-border flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-hidden bg-white p-3 sm:p-4 md:p-2"
      >
        <div className="mx-auto flex min-h-0 w-full min-w-0 max-w-5xl flex-1 flex-col border border-gray-200 rounded-2xl bg-[#F5F5F5]">
          <div className="hidden shrink-0 md:block">
            <SurveyHeader
              onDirectionChange={handleDirectionChange}
              organization={formData?.data?.organization}
              form={formData?.data}
              onCancel={() => navigate("/")}
            />
            <div className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h1 className="mb-1 mt-0 text-xl font-bold text-primary">
                    {formData?.data?.title}
                  </h1>
                  {formData?.data?.description ? (
                    <p className="mb-3 mt-0 text-sm leading-relaxed text-gray-500">
                      {formData?.data?.description}
                    </p>
                  ) : null}
                </div>
                {isAnonymous ? (
                  <div className="flex shrink-0 items-center gap-2 rounded-full bg-gray-50 px-3 py-1">
                    <span className="text-sm text-gray-700">
                      <span className="relative top-1">
                        <AnanymousIcon />
                      </span>{" "}
                      Anonymous Response
                    </span>
                  </div>
                ) : null}
              </div>
              <Progress
                percent={((currentQuestionIndex + 1) / totalQuestions) * 100}
                showInfo={false}
                strokeColor="#202835"
                className="mb-0"
              />
              <SurveySectionLabels
                sections={sectionLabels}
                activeSection={activeSection}
              />
            </div>
          </div>

          <div className="mb-3 shrink-0 md:hidden">
            <Collapse
              ghost
              expandIconPosition="end"
              className="rounded-2xl border border-gray-200 bg-white [&_.ant-collapse-header]:!flex-nowrap [&_.ant-collapse-header]:items-center [&_.ant-collapse-header]:gap-2 [&_.ant-collapse-expand-icon]:shrink-0"
              style={{
                background: formData?.data?.organization?.color || undefined,
              }}
              expandIcon={({ isActive }) => (
                <RightOutlined
                  rotate={isActive ? 90 : 0}
                  className="text-gray-500"
                />
              )}
              items={[
                {
                  key: "1",
                  label: (
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-nowrap items-center gap-2">
                        {orgLogoUrl && !orgLogoError ? (
                          <img
                            src={orgLogoUrl}
                            alt={formData?.data?.organization?.name}
                            className="h-8 w-8 shrink-0 rounded-full border border-gray-200 object-cover"
                            onError={() => setOrgLogoError(true)}
                          />
                        ) : (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/60">
                            <OrganizationsIcon />
                          </div>
                        )}
                        <div className="min-w-0">
                          <span className="block truncate font-medium">
                            {formData?.data?.organization?.name}
                          </span>
                          {/* {(formData?.data?.organization?.location?.name ||
                            formData?.data?.organization?.address) && (
                            <span className="block truncate text-xs text-gray-500">
                              {formData?.data?.organization?.location?.name ||
                                formData?.data?.organization?.address}
                            </span>
                          )} */}
                        </div>
                      </div>
                      {isAnonymous ? (
                        <div className="flex shrink-0 items-center gap-2 rounded-full bg-gray-50 px-3 py-1">
                          <span className="text-sm">
                            <span className="relative top-1">
                              <AnanymousIcon />
                            </span>{" "}
                            Anonymous
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ),
                  children: (
                    <div className="space-y-3 pt-2">
                      <h1 className="mb-0 mt-0 text-lg font-bold text-primary">
                        {formData?.data?.title}
                      </h1>
                      {formData?.data?.description ? (
                        <p className="mb-0 text-sm text-gray-500">
                          {formData?.data?.description}
                        </p>
                      ) : null}
                      <Progress
                        percent={
                          ((currentQuestionIndex + 1) / totalQuestions) * 100
                        }
                        showInfo={false}
                        strokeColor="#202835"
                        className="m-0 mb-0"
                      />
                      <SurveySectionLabels
                        sections={sectionLabels}
                        activeSection={activeSection}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>

          <Form
            form={form}
            onFinish={handleSubmit}
            initialValues={{
              terms: false,
              anonymous: false,
            }}
            layout="vertical"
            className="flex min-h-0 w-full min-w-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
              <div
                className={`box-border grid w-full min-w-0 grid-cols-1 items-start gap-4 overflow-x-hidden border-t border-gray-200 p-2 md:p-4 ${
                  currentQuestion?.video || hasUploadedRecordVideo
                    ? "md:grid-cols-[minmax(0,340px)_minmax(0,1fr)]"
                    : "md:grid-cols-1"
                }`}
              >
                {(currentQuestion?.video || hasUploadedRecordVideo) && (
                  <div className="hidden min-w-0 md:block">
                    <div className="w-full overflow-hidden rounded-3xl border border-gray-200 bg-white">
                      <CustomVideoPlayer
                        autoPlay
                        loop
                        preload="metadata"
                        src={currentQuestion?.video || ""}
                        videoUploaded={hasUploadedRecordVideo}
                        playsInline
                        wrapperClassName="rounded-none"
                      />
                    </div>
                  </div>
                )}

                <div className="min-w-0 max-w-full">
                  <div className="mb-3 rounded-3xl border border-gray-200 bg-white p-4 md:p-5">
                    <h3 className="mb-0 mt-0 flex flex-row items-start gap-2 font-bold text-gray-900">
                      {questionTitleNumber != null ? (
                        <span className="shrink-0 text-base tabular-nums leading-snug sm:text-lg">
                          {questionTitleNumber})
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1 break-words text-lg leading-snug sm:text-xl md:text-lg">
                        {currentQuestion?.title}
                      </span>
                    </h3>
                    {currentQuestion?.description ? (
                      <p className="mb-0 mt-2 text-sm text-gray-500">
                        {currentQuestion?.description}
                      </p>
                    ) : null}
                  </div>

                  <div className="mb-1 rounded-3xl border border-gray-200 bg-white p-3 md:p-5">
                    {(currentQuestion?.video || hasUploadedRecordVideo) && (
                      <div className="mb-3 w-full overflow-hidden rounded-3xl border border-gray-200 bg-white md:hidden">
                        <CustomVideoPlayer
                          autoPlay
                          loop
                          preload="metadata"
                          src={currentQuestion?.video || ""}
                          videoUploaded={hasUploadedRecordVideo}
                          playsInline
                          wrapperClassName="rounded-none"
                        />
                      </div>
                    )}

                    <div className="grid min-w-0 grid-cols-1 gap-3">
                      {getVisibleInputEntries(
                        currentQuestion?.response,
                        watchedResponses,
                      ).map(({ input, index }) => (
                        <DynamicInput
                          key={input._id || `${input.input_title}-${index}`}
                          input={input}
                          form={form}
                          index={index}
                        />
                      ))}
                    </div>

                    {currentQuestionIndex === 0 && (
                      <div className="mt-3 space-y-1">
                        {!formData?.data?.anonymous &&
                          currentQuestion?.showAnonymous && (
                            <Form.Item
                              className="mb-0"
                              name="anonymous"
                              valuePropName="checked"
                            >
                              <Checkbox onChange={handleAnonymousChange}>
                                Make My Response{" "}
                                <span>
                                  Anonymous
                                  <span className="relative top-1">
                                    <AnanymousIcon />
                                  </span>
                                </span>
                              </Checkbox>
                            </Form.Item>
                          )}
                        {currentQuestion?.showTerms && (
                          <Form.Item
                            className="mb-0"
                            name="terms"
                            valuePropName="checked"
                            rules={[
                              {
                                validator: async (_, value) => {
                                  if (!value) {
                                    throw new Error(
                                      "Please accept the terms & conditions",
                                    );
                                  }
                                },
                              },
                            ]}
                            required
                          >
                            <Checkbox>
                              Accept our{" "}
                              <a
                                target="terms-and-conditions"
                                href={organizationTermsRoute}
                                className="cursor-pointer underline"
                              >
                                Terms & Conditions
                              </a>{" "}
                              and{" "}
                              <a
                                target="_blank"
                                href={organizationPrivacyRoute}
                                className="cursor-pointer underline"
                                rel="noreferrer"
                              >
                                Privacy Policy
                              </a>
                            </Checkbox>
                          </Form.Item>
                        )}
                      </div>
                    )}

                    {direction === "vertical" && <BottomNavigation />}
                  </div>
                </div>
              </div>
            </div>

            {direction !== "vertical" &&
              !(
                formData?.data?.status !== "active" &&
                getNavigationButtonText() === "Submit"
              ) && (
                <div className="mt-auto flex shrink-0 justify-end border-t border-gray-100 bg-white py-4 px-4">
                  <Button
                    type="primary"
                    loading={isSubmitting}
                    htmlType="submit"
                    className="flex w-full cursor-pointer items-center justify-center rounded-2xl px-8 py-2 shadow-lg sm:w-auto"
                  >
                    {getNavigationButtonText()}
                  </Button>
                </div>
              )}
          </Form>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuestionPage;
